import { Button } from "@/components/ui/button";
import { ChevronUp, ChevronDown, GripVertical, Copy } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface ChecklistTemplate {
  id: number | string;
  stage_id?: number;
  stage_name?: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  checklist_type?: string;
  display_order?: number;
}

interface Stage {
  id: number;
  name: string;
  stage_order: number;
}

interface ChecklistCardProps {
  template: ChecklistTemplate;
  index: number;
  totalCount: number;
  stages: Stage[];
  checklistItems: Record<string, any[]>;
  loading: boolean;
  type: 'residential' | 'commercial';
  onMoveUp: (template: ChecklistTemplate, index: number, type: 'residential' | 'commercial') => void;
  onMoveDown: (template: ChecklistTemplate, index: number, type: 'residential' | 'commercial') => void;
  onManageItems: (template: ChecklistTemplate) => void;
  onEdit: (template: ChecklistTemplate) => void;
  onDelete: (template: ChecklistTemplate) => void;
  dragHandleProps?: any;
  isDragging?: boolean;
  templateMode?: boolean;
}

export const ChecklistCard = ({
  template,
  index,
  totalCount,
  stages,
  checklistItems,
  loading,
  type,
  onMoveUp,
  onMoveDown,
  onManageItems,
  onEdit,
  onDelete,
  dragHandleProps,
  isDragging,
  templateMode = false
}: ChecklistCardProps) => {
  const { toast } = useToast();

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied!",
        description: `${label} copied to clipboard`,
        duration: 2000,
      });
    } catch (error) {
      console.error('Failed to copy:', error);
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  return (
    <div className={`flex justify-between items-center border p-4 rounded-md ${isDragging ? 'opacity-50' : ''}`}>
      <div className="flex items-start gap-3">
        <div className="flex items-center gap-3">
          {/* Order number */}
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted text-sm font-medium text-foreground">
            {index + 1}
          </div>
          
          {/* Drag handle */}
          {dragHandleProps && (
            <div
              {...dragHandleProps}
              className="cursor-grab hover:cursor-grabbing"
            >
              <GripVertical className="w-4 h-4 text-muted-foreground" />
            </div>
          )}
          
          {/* Legacy up/down buttons (can be removed later) */}
          <div className="flex flex-col items-center justify-center space-y-1">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-7 w-7"
              onClick={() => onMoveUp(template, index, type)}
              disabled={index === 0 || loading}
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-7 w-7"
              onClick={() => onMoveDown(template, index, type)}
              disabled={index === totalCount - 1 || loading}
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2 group">
            <h4 className="font-medium text-foreground">{template.name}</h4>
            <button
              onClick={() => copyToClipboard(String(template.id), 'Template ID')}
              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-200 rounded"
              title="Copy template ID"
            >
              <Copy className="h-3.5 w-3.5 text-gray-600" />
            </button>
          </div>
          {template.description && (
            <p className="text-sm text-muted-foreground">{template.description}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            ID: {template.id}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Stage: {templateMode
              ? (template.stage_name || 'Unknown')
              : (stages.find(s => s.id === template.stage_id)?.name || 'Unknown')
            }
          </p>
          <p className="text-xs text-muted-foreground">
            Items: {checklistItems[template.id]?.length || 0}
          </p>
        </div>
      </div>
      <div className="flex space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onManageItems(template)}
        >
          Manage Items
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onEdit(template)}
        >
          Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDelete(template)}
        >
          Delete
        </Button>
      </div>
    </div>
  );
};
